<template>
  <div class="earning-rule-manager">
    <!-- Header -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h3 class="text-lg font-semibold text-gray-900">{{ $t('loyalty.title.rules') }}</h3>
        <button
          @click="showCreateDialog = true"
          class="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm hover:shadow-md">
          <i class="mdi mdi-plus mr-2"></i>
          {{ $t('loyalty.button.add_rule') }}
        </button>
      </div>

      <!-- Rules List -->
      <div class="p-6" v-loading="loading">
        <div v-if="rules.length === 0" class="text-center py-12">
          <i class="mdi mdi-information-outline text-4xl text-gray-400 mb-2"></i>
          <p class="text-gray-500">{{ $t('message.no_data') }}</p>
        </div>
        <div v-else class="space-y-4">
          <div
            v-for="rule in rules"
            :key="rule.id"
            class="bg-white border-2 rounded-xl p-6 hover:shadow-lg transition-all duration-200"
            :class="rule.is_active ? 'border-green-300' : 'border-gray-200'">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="flex items-center space-x-3 mb-3">
                  <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-blue-600 text-white">
                    Priority: {{ rule.priority || 0 }}
                  </span>
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {{ rule.rule_type }}
                  </span>
                  <span
                    v-if="rule.is_active"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {{ $t('loyalty.status.active') }}
                  </span>
                </div>
                <h4 class="text-lg font-bold text-gray-900 mb-2">{{ rule.name }}</h4>
                <p class="text-sm text-gray-600 mb-4">{{ rule.description }}</p>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-gray-50 rounded-lg p-3">
                    <p class="text-xs text-gray-500 mb-1">{{ $t('loyalty.label.points_multiplier') }}</p>
                    <p class="text-lg font-bold text-blue-600">{{ parseFloat(rule.points_multiplier).toFixed(2) }}x</p>
                  </div>
                  <div v-if="rule.customer_segment" class="bg-gray-50 rounded-lg p-3">
                    <p class="text-xs text-gray-500 mb-1">{{ $t('loyalty.label.customer_segment') }}</p>
                    <p class="text-sm font-medium text-gray-900">{{ rule.customer_segment }}</p>
                  </div>
                </div>
                <div v-if="rule.valid_from || rule.valid_until" class="mt-3 text-xs text-gray-500">
                  Valid: {{ rule.valid_from ? $shortDate(rule.valid_from) : 'Always' }}
                  {{ rule.valid_until ? ` - ${$shortDate(rule.valid_until)}` : '' }}
                </div>
              </div>
              <div class="ml-4 flex flex-col space-y-2">
                <button
                  @click="editRule(rule)"
                  class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors">
                  <i class="mdi mdi-pencil mr-1.5"></i>
                  {{ $t('button.edit') }}
                </button>
                <button
                  @click="deleteRule(rule.id)"
                  class="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-lg text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors">
                  <i class="mdi mdi-delete mr-1.5"></i>
                  {{ $t('button.delete') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Rule Dialog -->
    <rule-form-dialog
      :visible="showCreateDialog || showEditDialog"
      :rule="selectedRule"
      :program-id="programId"
      @saved="handleRuleSaved"
      @cancelled="closeDialog">
    </rule-form-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'
import RuleFormDialog from './RuleFormDialog.vue'

export default {
  name: 'EarningRuleManager',
  components: {
    RuleFormDialog
  },
  mixins: [ErrorHandlingMixin],
  props: {
    programId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      rules: [],
      loading: false,
      showCreateDialog: false,
      showEditDialog: false,
      selectedRule: null
    }
  },
  async mounted () {
    await this.fetchRules()
  },
  methods: {
    async fetchRules () {
      try {
        this.loading = true
        this.rules = await this.$store.dispatch('loyalty/rules/fetchRules', this.programId)
      } catch (error) {
        console.error('Error fetching rules:', error)
      } finally {
        this.loading = false
      }
    },
    editRule (rule) {
      this.selectedRule = rule
      this.showEditDialog = true
    },
    async deleteRule (ruleId) {
      this.askQuestion(this.$t('loyalty.confirm.delete_rule'), async () => {
        try {
          await this.withLoading(async () => {
            await this.$store.dispatch('loyalty/rules/deleteRule', {
              id: ruleId,
              programId: this.programId
            })
            await this.fetchRules()
            this.showSuccess(this.$t('loyalty.message.rule_deleted'))
            this.$emit('rule-deleted', ruleId)
          }, 'loading')
        } catch (error) {
          console.error('Error deleting rule:', error)
        }
      })
    },
    handleRuleSaved () {
      this.closeDialog()
      this.fetchRules()
      this.$emit('rule-created')
    },
    closeDialog () {
      this.showCreateDialog = false
      this.showEditDialog = false
      this.selectedRule = null
    }
  }
}
</script>

<style scoped>
</style>
